import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWrench, faCheck, faArrowRight, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import { useLocation } from 'react-router-dom';
import AppContext from '../context/AppContext';
import TryAPI, { TryGame } from '../api/TryAPI';
import { Platform } from '../types/Platform';
import { useAppDispatch, useAppSelector } from '../store';
import { openRepairAt, closeRepair } from '../reducers/uiReducer';
import { setParams } from '../reducers/paramsReducer';
import { analytics } from '../utils/analytics';
import RepairModal from './RepairModal';
import PlayOutModal from './PlayOutModal';
import BlunderCard from './BlunderCard';

// /try: the front door for someone arriving from a link with only a username. About half a minute
// later they are looking at a mistake they actually made, asked to find the move they missed.
//
// Not a report: the page is a runway to the repair session, and the visitor's one action is a move.
// Two games demo repair honestly but not patterns (those need a library, which an account buys), and
// the copy says so.

type Phase = 'form' | 'working' | 'failed' | 'nothing' | 'repairing' | 'done';

// Roughly one game's engine time, used to ease the progress bar between real checkpoints and to
// state the wait up front. Measured over 94 uncached evaluations: median 19.4s, p90 31.9s. A
// first-time visitor's positions are never cached.
const ASSUMED_MS_PER_GAME = 20000;

function platformFromQuery(value: string | null): Platform {
  const v = (value ?? '').toLowerCase();
  if (v === 'lichess' || v === 'lichess.org' || v === '1') {
    return Platform.Lichess;
  }
  return Platform.ChessCom;
}

function platformLabel(platform: Platform): string {
  return platform === Platform.Lichess ? 'Lichess' : 'Chess.com';
}

// Which side the visitor had, from the resolved handle. Null when the names don't line up, rather
// than guess wrong.
function colourPlayed(game: TryGame, handle: string): string | null {
  const me = handle.trim().toLowerCase();
  if (me === '') {
    return null;
  }
  if ((game.White ?? '').toLowerCase() === me) {
    return 'as White';
  }
  if ((game.Black ?? '').toLowerCase() === me) {
    return 'as Black';
  }
  return null;
}

export default function TryContainer(): React.ReactNode {
  const dispatch = useAppDispatch();
  const { isAuthenticated, openLogin } = useContext(AppContext);
  const { search } = useLocation();
  const query = useMemo(() => new URLSearchParams(search), [search]);
  const repairGameIds = useAppSelector((state) => state.uiReducer.repairGameIds);

  // The link carries the answers but they still press the button: auto-starting would spend a download
  // and engine runs on every crawler and accidental click.
  const [username, setUsername] = useState(query.get('username') ?? query.get('handle') ?? '');
  const [platform, setPlatform] = useState<Platform>(platformFromQuery(query.get('platform')));

  const [phase, setPhase] = useState<Phase>('form');
  const [error, setError] = useState<string | null>(null);
  const [games, setGames] = useState<TryGame[]>([]);
  const [handle, setHandle] = useState('');
  const [groupToken, setGroupToken] = useState('');
  const [summary, setSummary] = useState<{ total: number; minWeight: number } | null>(null);
  // Checkpoint reached, out of find + one per game + count.
  const [step, setStep] = useState(0);
  const [steps, setSteps] = useState(2);
  const [stepStartedAt, setStepStartedAt] = useState(0);
  const [now, setNow] = useState(0);
  // Guards against a second run from a double click or re-render.
  const runningRef = useRef(false);
  // Focused when the button is pressed with an empty handle.
  const handleFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    analytics.pageview('/try', 'Try — repair your mistakes');
  }, []);

  // Ticks the progress ring between checkpoints. One interval for the whole working phase.
  useEffect(() => {
    if (phase !== 'working') {
      return;
    }
    const id = window.setInterval(() => setNow(Date.now()), 200);
    return () => window.clearInterval(id);
  }, [phase]);

  // The session closing ends the flow, not the page.
  useEffect(() => {
    if (phase === 'repairing' && repairGameIds == null) {
      setPhase('done');
    }
  }, [phase, repairGameIds]);

  // The sign-up offer is on screen; once per arrival.
  useEffect(() => {
    if (phase === 'done' || phase === 'nothing') {
      analytics.track('try_offer_seen', { outcome: phase, mistakes: summary?.total ?? 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Don't strand the overlay over the next page.
  useEffect(() => () => { dispatch(closeRepair()); }, [dispatch]);

  const run = async () => {
    if (runningRef.current) {
      return;
    }
    // An empty handle (e.g. arriving from the home band with `?handle=`) says what's missing and focuses
    // the box, rather than a dead disabled button.
    if (username.trim() === '') {
      setError(`Type the handle you play under on ${platformLabel(platform)} first.`);
      handleFieldRef.current?.focus();
      return;
    }
    runningRef.current = true;
    setPhase('working');
    setError(null);
    setGames([]);
    setSummary(null);
    setStep(0);
    setSteps(2);
    setStepStartedAt(Date.now());
    setNow(Date.now());
    analytics.track('import_start', { ui_source: 'try', platform: platformLabel(platform) });

    try {
      const started = await TryAPI.start(username.trim(), platform);
      if (started.Error != null || started.Games.length === 0) {
        setError(started.Error ?? 'We couldn’t find any recent games for that username.');
        setPhase('failed');
        // Pairs with import_start: this handle produced nothing. Distinct from a failed request (catch below).
        analytics.track('import_result', { ui_source: 'try', found: 'none' });
        return;
      }
      setHandle(started.Handle);
      setGroupToken(started.GroupToken);
      setGames(started.Games);
      // Now the real shape of the work is known: find + one per game + the count.
      setSteps(started.Games.length + 2);
      setStep(1);
      setStepStartedAt(Date.now());
      analytics.track('import_result', { ui_source: 'try', found: 'yes', games: started.Games.length });

      // One game at a time: the engine is serialized anyway, and the page can say which one it's on. Each
      // call returns when that game is evaluated.
      for (let i = 0; i < started.Games.length; i++) {
        const game = started.Games[i];
        await TryAPI.evaluate(game.Id);
        setGames((current) =>
          current.map((g) => (g.Id === game.Id ? { ...g, Evaluated: true } : g)),
        );
        setStep(i + 2);
        setStepStartedAt(Date.now());
      }
      analytics.track('evaluate_game', { ui_source: 'try', games: started.Games.length });

      const ids = started.Games.map((g) => g.Id);
      const found = await TryAPI.summary(ids, started.Handle);
      setSummary({ total: found.Total, minWeight: found.SuggestedMinWeight });
      setStep(started.Games.length + 2);

      if (found.Total === 0) {
        setPhase('nothing');
        return;
      }

      // The session's point of view must be the visitor's side, so the handle goes into the params.
      dispatch(setParams({ username: started.Handle }));
      dispatch(openRepairAt({ gameIds: ids, minWeight: found.SuggestedMinWeight }));
      setPhase('repairing');
      // The point of the page: a stranger is looking at a position from their own game.
      analytics.track('try_complete', { games: started.Games.length, mistakes: found.Total });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Try again in a moment.');
      setPhase('failed');
    } finally {
      runningRef.current = false;
    }
  };

  const reopen = () => {
    if (games.length === 0) {
      return;
    }
    dispatch(setParams({ username: handle }));
    dispatch(openRepairAt({
      gameIds: games.map((g) => g.Id),
      minWeight: summary?.minWeight ?? 4,
    }));
    setPhase('repairing');
  };

  // Real checkpoints, eased between; it can run ahead inside a step but never past the next checkpoint.
  const percent = useMemo(() => {
    if (phase === 'form' || phase === 'failed') return 0;
    if (phase !== 'working') return 100;
    const done = step / steps;
    const next = (step + 1) / steps;
    const elapsed = Math.max(0, now - stepStartedAt);
    const within = Math.min(0.92, elapsed / ASSUMED_MS_PER_GAME);
    return Math.round(Math.min(next, done + (next - done) * within) * 100);
  }, [phase, step, steps, now, stepStartedAt]);

  const workingLabel =
    step === 0
      ? `Finding your latest games on ${platformLabel(platform)}…`
      : step <= games.length
        ? `Reading game ${step} of ${games.length} with the engine…`
        : 'Looking for the moves that cost you…';

  return (
    <div className="cm-try">
      <RepairModal />
      <PlayOutModal />

      {phase === 'form' || phase === 'failed' ? (
        <div className="cm-try-card">
          <h2 className="cm-try-card-title">Repair your mistakes</h2>
          <p className="cm-try-card-sub">
            Repair means going back to a position from your own recent games and playing the move
            you missed.
          </p>

          <Form
            onSubmit={(e) => {
              e.preventDefault();
              run();
            }}
          >
            <Form.Group className="cm-try-field">
              <Form.Label>Where you play</Form.Label>
              <Form.Select
                value={platform}
                onChange={(e) => setPlatform(Number(e.target.value) as Platform)}
              >
                <option value={Platform.ChessCom}>Chess.com</option>
                <option value={Platform.Lichess}>Lichess.org</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="cm-try-field">
              <Form.Label>Your handle</Form.Label>
              <Form.Control
                name="handle"
                ref={handleFieldRef}
                value={username}
                autoFocus
                placeholder="your handle on that site"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                data-lpignore="true"
                data-1p-ignore="true"
                onChange={(e) => {
                  setUsername(e.target.value);
                  if (error != null) {
                    setError(null);
                  }
                }}
              />
            </Form.Group>

            {error != null ? <div className="cm-try-error">{error}</div> : null}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="cm-try-cta"
            >
              <FontAwesomeIcon icon={faWrench} className="me-2" />
              Repair Your Mistakes
            </Button>
            <p className="cm-try-fineprint">
              No account, no password. Your games are public on {platformLabel(platform)} already.
            </p>
          </Form>
        </div>
      ) : null}

      {phase === 'working' ? (
        <div className="cm-try-card cm-try-working">
          <ProgressRing percent={percent} />
          <div className="cm-try-working-label">{workingLabel}</div>
          <ul className="cm-try-games">
            {games.map((g) => (
              <li key={g.Id} className={`cm-try-game${g.Evaluated ? ' is-done' : ''}`}>
                <span className="cm-try-game-check">
                  {g.Evaluated ? (
                    <FontAwesomeIcon icon={faCheck} />
                  ) : (
                    <Spinner animation="border" size="sm" />
                  )}
                </span>
                <span className="cm-try-game-players">
                  {g.White ?? '?'} vs {g.Black ?? '?'}
                </span>
                <span className="cm-try-game-opening">
                  {g.Opening ?? 'Opening not named'}
                  {colourPlayed(g, handle) != null ? ` · ${colourPlayed(g, handle)}` : ''}
                </span>
                <span className="cm-try-game-meta">
                  {new Date(g.Date).toLocaleDateString()}
                  {g.TimeControl ? ` · ${g.TimeControl}` : ''}
                </span>
              </li>
            ))}
          </ul>
          <p className="cm-try-fineprint">
            The engine is running now, on your games. This takes about
            {' '}{Math.max(10, (games.length || 2) * Math.round(ASSUMED_MS_PER_GAME / 1000))}{' '}
            seconds.
          </p>
        </div>
      ) : null}

      {/* While the engine works, one real mistake from someone else's game. Below the progress, so
          the ring still shows the wait is real work. */}
      {phase === 'working' ? <BlunderCard /> : null}

      {phase === 'nothing' ? (
        <div className="cm-try-card cm-try-outcome">
          <h2 className="cm-try-card-title">Clean pair of games.</h2>
          <p className="cm-try-card-sub">
            The engine went through {games.length === 1 ? 'your last game' : `your last ${games.length} games`}
            {' '}as {handle} and didn’t find a move worth repairing. That happens — and it’s exactly
            why two games isn’t enough to work from.
          </p>
          <SignUpCta
            isAuthenticated={isAuthenticated}
            openLogin={openLogin}
            outcome="nothing"
            label="Bring in your whole history"
          />
        </div>
      ) : null}

      {phase === 'done' ? (
        <div className="cm-try-card cm-try-outcome">
          <h2 className="cm-try-card-title">That was two games.</h2>
          <p className="cm-try-card-sub">
            {summary != null
              ? `The engine found ${summary.total} ${summary.total === 1 ? 'move' : 'moves'} worth repairing in them.`
              : ''}{' '}
            Two games can show you a mistake. It takes a few hundred to show you the mistake you
            keep making — and that’s the one worth losing.
          </p>
          <div className="cm-try-outcome-actions">
            <Button variant="outline-secondary" onClick={reopen}>
              <FontAwesomeIcon icon={faRotateRight} className="me-2" />
              Repair them again
            </Button>
            <SignUpCta
              isAuthenticated={isAuthenticated}
              openLogin={openLogin}
              outcome="done"
              label="Import every game you’ve played"
            />
          </div>
          {groupToken !== '' ? (
            <p className="cm-try-fineprint">
              These games are held under a temporary link and aren’t part of anyone’s history yet.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// Where the funnel lands: an account turns a demo into a library. Signed-in visitors get the import
// route instead.
function SignUpCta(props: {
  isAuthenticated: boolean;
  openLogin: () => void;
  outcome: 'done' | 'nothing';
  label: string;
}): React.ReactNode {
  const trackClick = () => {
    analytics.track('try_signup_click', { outcome: props.outcome, signed_in: props.isAuthenticated });
  };
  if (props.isAuthenticated) {
    return (
      <Button variant="primary" href="/#/?import=1" onClick={trackClick}>
        {props.label}
        <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
      </Button>
    );
  }
  return (
    <Button
      variant="primary"
      onClick={() => {
        trackClick();
        props.openLogin();
      }}
    >
      {props.label}
      <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
    </Button>
  );
}

// An SVG ring rather than a bar: it reads as "working on something specific".
export function ProgressRing(props: { percent: number }): React.ReactNode {
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - Math.min(100, Math.max(0, props.percent)) / 100);
  return (
    <div className="cm-try-ring" role="progressbar" aria-valuenow={props.percent}
      aria-valuemin={0} aria-valuemax={100}>
      <svg viewBox="0 0 120 120" aria-hidden="true">
        <circle className="cm-try-ring-track" cx="60" cy="60" r={radius} />
        <circle
          className="cm-try-ring-fill"
          cx="60"
          cy="60"
          r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <span className="cm-try-ring-value">{props.percent}%</span>
    </div>
  );
}
